// src/pages/feedback/components/FeedbackStatusMenu.tsx
//
// 상태 변경 드롭다운. **작성자가 아니어도 누구나** 쓸 수 있다(총괄 확정 —
// 소규모 팀 트리아지). 확인 다이얼로그는 두지 않는다: 되돌리기가 한 번의
// 클릭이고, 대신 낙관적 갱신 + 실패 시 롤백 + 스낵바로 알린다.
//
// ToggleButtonGroup 4칸이 아니라 Menu 인 이유: 헤더 우측에서 4칸 토글은
// 모바일에서 줄바꿈되고 액션 버튼(수정/삭제)과 폭 경쟁을 한다.
//
// 각 MenuItem 은 해당 상태 칩만 렌더한다 — 칩 자체가 이미 한글 라벨을
// 품고 있어 라벨을 따로 붙이면 같은 단어가 두 번 나온다. 현재 값은
// selected(액센트 틴트) + 체크로 두 채널에서 표시한다.

import { useState } from "react";
import { Box, Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { designTokens } from "../../../theme.ts";
import type { FeedbackStatus } from "../../../contracts/feedback/types";
import { STATUS_ORDER, statusLabel } from "./feedbackVisuals.ts";
import FeedbackStatusChip from "./FeedbackStatusChip.tsx";

const T = designTokens;

interface FeedbackStatusMenuProps {
  value: FeedbackStatus;
  onChange: (next: FeedbackStatus) => void | Promise<void>;
  /** 변경 요청 진행 중 */
  disabled?: boolean;
  fullWidth?: boolean;
}

export default function FeedbackStatusMenu({
  value,
  onChange,
  disabled = false,
  fullWidth = false,
}: FeedbackStatusMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSelect = (next: FeedbackStatus) => {
    setAnchorEl(null);
    if (next === value) return; // 서버도 멱등이지만 왕복 자체를 아낀다
    void onChange(next);
  };

  return (
    <>
      <Button
        variant="outlined"
        disabled={disabled}
        fullWidth={fullWidth}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        // 현재 값을 접근성 이름에 반드시 포함한다. "처리 상태 변경"만 두면
        // 안에 렌더된 칩의 라벨("접수"/"검토중"/…)이 이름에서 지워져
        // 스크린 리더 사용자는 현재 상태를 알 방법이 없고, 눈에 보이는 텍스트가
        // 이름에 없어 WCAG 2.5.3(Label in Name)에도 어긋난다.
        aria-label={`처리 상태: ${statusLabel(value)} — 변경하려면 선택하세요`}
        sx={{
          gap: 0.75,
          px: 1.25,
          py: 0.75,
          fontFamily: T.fonts.sans,
          fontWeight: 500,
          justifyContent: fullWidth ? "space-between" : "center",
        }}
      >
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          {disabled ? (
            <CircularProgress size={16} sx={{ color: "#6B7178" }} />
          ) : null}
          <FeedbackStatusChip status={value} />
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#6B7178" }} />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {STATUS_ORDER.map((s) => (
          <MenuItem
            key={s}
            selected={s === value}
            onClick={() => handleSelect(s)}
            sx={{ gap: 1.25, minWidth: 168, justifyContent: "space-between" }}
          >
            <FeedbackStatusChip status={s} />
            {s === value ? (
              <CheckIcon sx={{ fontSize: 16, color: "#C93A14" }} />
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
